import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, createBlock as _createBlock } from "vue"
import _imports_0 from '../../../../Content/images/Providers/pathlines.png'


const _hoisted_1 = { class: "container-fluid px-0 mt-0" }
const _hoisted_2 = { class: "container" }
const _hoisted_3 = { class: "row align-items-center" }
const _hoisted_4 = { class: "col-md-2 d-flex justify-content-center align-items-center" }
const _hoisted_5 = { class: "container" }
const _hoisted_6 = { class: "row align-items-center pb-1 mb-4" }
const _hoisted_7 = { class: "col-md-2 d-flex justify-content-center align-items-center" }
const _hoisted_8 = ["innerHTML"]

import { ref, computed, watch } from "vue";
import SubSection from "../common/SubSection.vue";
import InvestmentAllocation from "./InvestmentAllocation.vue";
import QuestionAndAnswer from "../common/QuestionAndAnswer.vue";
import Apply from "./Apply.vue";
import PensionPotsViewer from "../assets/PensionPotsViewer.vue";
import { useStore } from "vuex";
import { Retiree } from "../../ts/retiree";
import { SecureSection } from "../../ts/secure/secureSection";
import questions from "../../ts/secure/questions";
import { app } from "../../ts/app";
import ScenarioOutcome from "../../ts/scenarioOutcome";
import Temporal from "@/shared/ts/temporal";
import Constants from "../../ts/constants";
import { ApplicationInfo } from "../../ts/secure/applicationInfo";
import { FundAllocationInstruction } from "@/guiide/ts/secure/fundAllocationInstruction";
import Result from "@/guiide/ts/result";
import FundAllocationCalculator from "@/guiide/ts/secure/fundAllocationCalculator";
import fundInfo from "@/guiide/ts/funds";


export default /*@__PURE__*/_defineComponent({
  __name: 'Product',
  emits: ["back"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const store = useStore();
const investmentAllocation = ref(null);
const apply = ref(null);
const retiree = computed<Retiree | null>(() => store.state.retiree);
const eligiblePensionPotIds = ref<number[]>([]);
const selectedPensionPotIds = ref<number[]>([]);
const questionAndAnswerQuestions = () => questions.filter(q => q.section == SecureSection.QuestionsAndAnswers);
const ineligibleReason = ref("");
const invalidAllocationsFound = ref(false);
const isApplying = ref(false);
const applyErrorMessage = ref("");
let invalidAllocationsReason = "";
let selectedPensionPotIdsInitialised = false;

watch(
    () => retiree,
    (newRetiree) => {
        if (selectedPensionPotIdsInitialised || !newRetiree?.value?.pensionPots) {
            return;
        }

        selectedPensionPotIds.value = newRetiree.value.pensionPots.pots.filter(pp => pp.employerMonthlyContribution === 0).map(pp => pp.id);
        eligiblePensionPotIds.value = Array.from(selectedPensionPotIds.value);
        selectedPensionPotIdsInitialised = true;
    },
    { immediate: true }
)

const productLive = computed<boolean>(() => { return store.state.configuration.secureProductApplyActive; });

async function onApply() {
    isApplying.value = true;
    applyErrorMessage.value = "";

    if (selectedPensionPotIds.value.length == 0) {
        applyErrorMessage.value = "Please select the pensions you wish to transfer.";
        isApplying.value = false;
        return;
    }

    const allocationInfo: FundAllocationInstruction = investmentAllocation.value.getAllocationInstruction();

    if (!allocationInfo.isValid) {
        applyErrorMessage.value = allocationInfo.invalidMessage;
        isApplying.value = false;
        return;
    }

    const additionalInfoValidationResult: Result = apply.value.validateAdditionalInfo();
    if (!additionalInfoValidationResult.successful) {
        applyErrorMessage.value = additionalInfoValidationResult.message;
        isApplying.value = false;
        return;
    }

    const additionalInfo = apply.value.getAdditionalInfo();
    const updatePersonalInfoResult = await app.updateAdditionalPersonalInfo(additionalInfo);

    if (!updatePersonalInfoResult.successful) {
        applyErrorMessage.value = updatePersonalInfoResult.message;
        isApplying.value = false;
        return;
    }

    const applicationInfo: ApplicationInfo = {
        pensionPotIds: selectedPensionPotIds.value,
        matchedYears: allocationInfo.matchedYears,
        matchedYearsInWindow: allocationInfo.matchedYearsInWindow,
        taxFreeLumpSumFundId: allocationInfo.taxFreeLumpSumFundId,
        fundAllocCalcAtPresent: allocationInfo.fundAllocCalcAtPresent,
        fundAllocCalcAtRetirement: allocationInfo.fundAllocCalcAtRetirement
    };

    const result = await app.beginPensionTransfer(applicationInfo);

    if (!result.successful) {
        applyErrorMessage.value = result.message;
    }

    isApplying.value = false;
}

const isEligibleForProduct = computed<boolean>(() => {
    ineligibleReason.value = "";

    if (retiree.value == null) {
        return true;
    }

    if (retiree.value.alreadyDrawingPension) {
        ineligibleReason.value = "You are already drawing your pension.";
        return false;
    }

    if (retiree.value.pensionPots.pots.length == 0) {
        ineligibleReason.value = "You do not have any pension pots.";
        return false;
    }

    if (retiree.value.pensionPots.pots.every(pp => pp.employerMonthlyContribution > 0)) {
        ineligibleReason.value = "You do not have any eligible pension pots as all of your pots have an employer contribution.";
        return false;
    }

    const currentAge = Temporal.calcAge(new Date(retiree.value.dateOfBirth));

    if (currentAge < Constants.Product.MinEligibleAge) {
        ineligibleReason.value = `You are younger than the minimum product age of ${Constants.Product.MinEligibleAge}.`;
        return false;
    }

    if (invalidAllocationsFound.value) {
        ineligibleReason.value = invalidAllocationsReason;
        return false;
    }

    const accordingToPlanScenario = store.state.accordingToPlanScenario;

    if (accordingToPlanScenario == null) {
        return true;
    }

    if (accordingToPlanScenario.outcome == ScenarioOutcome.Fail) {
        ineligibleReason.value = "You have a shortfall in your 'according to plan' scenario.";
        return false;
    }

    return true;
});

function investmentAllocationsInvalid(reason: string) {
    invalidAllocationsFound.value = true;
    invalidAllocationsReason = reason;
}

function onAboutClicked(url: string) {
    window.open(url, "_blank");
}

const pensionsViewerCurrentValueHelpText: Array<string> = [
    "These figures are estimates based on the latest known pot value you entered. These include estimated investment returns at the rate you assumed in your plan, plus any contributions you have entered since that date.",
    "These values will fluctuate daily, and the exact figures will only be known when transferred to Pathlines."
];


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _cache[24] || (_cache[24] = _createElementVNode("h3", null, "Guidance", -1)),
    _createElementVNode("div", _hoisted_1, [
      (!isEligibleForProduct.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _cache[4] || (_cache[4] = _createElementVNode("p", null, "You are not eligible for the integrated product.", -1)),
            _createElementVNode("p", null, _toDisplayString(ineligibleReason.value), 1)
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("p", null, [
              _cache[5] || (_cache[5] = _createElementVNode("strong", null, "Guiide", -1)),
              _cache[6] || (_cache[6] = _createTextVNode(" has partnered with ")),
              _cache[7] || (_cache[7] = _createElementVNode("strong", null, "Pathlines", -1)),
              _cache[8] || (_cache[8] = _createTextVNode(", a pension provider, to help simplify getting the plan payments wanted, track the progress of any plan and understand the investment risks involved. It is aimed at those near or at-retirement. This pension product is called ")),
              _createElementVNode("strong", null, _toDisplayString(_unref(Constants).Product.Name), 1),
              _cache[9] || (_cache[9] = _createTextVNode(". "))
            ]),
            _cache[21] || (_cache[21] = _createElementVNode("p", null, " It is the only product available where Guiide can provide the plan payments wanted. It is also the only product that can send back latest fund value information to Guiide. These can then be input to update your calculations to see if your plan remains on track. Given these unique features it is not possible to directly compare any similar products. If more are available in future these will be added. There may be other products which are better suited to you. ", -1)),
            _createVNode(SubSection, {
              title: `Who are PathLines, the entity providing ${_unref(Constants).Product.Name}?`,
              showHelp: false
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "col-md-2 d-flex justify-content-center align-items-center" }, [
                      _createElementVNode("img", {
                        src: _imports_0,
                        class: "d-block mx-auto img-fluid"
                      })
                    ], -1)),
                    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "col-md-8 ps-4" }, [
                      _createElementVNode("p", null, " Pathlines are an FCA regulated Self Invested Personal Pensions (SIPP) provider with many years experience of administering pensions for customers in both the pre and post retirement world. They currently administer around £2bn of customers pension assets. "),
                      _createElementVNode("p", null, [
                        _createTextVNode(" Pathlines will be your pension provider and they will do everything for you. Taking your Guiide plan and the pension information you have entered into your Guiide portal, they will consolidate your pensions, receive contributions and invest your money "),
                        _createElementVNode("strong", null, "in the Funds you choose"),
                        _createTextVNode(". ")
                      ]),
                      _createElementVNode("p", null, " When the time comes Pathlines will pay your income payments in line with your Guiide plan. "),
                      _createElementVNode("p", null, [
                        _createTextVNode(" You are able to amend your Guiide plan throughout your journey to suit your income requirements and anytime you do this Guiide will let Pathlines know so your new instructions can be processed for you. You can view your pension value with Pathlines via their online portal or "),
                        _createElementVNode("strong", null, "within Guiide"),
                        _createTextVNode(" at anytime. You can learn more about Pathlines by clicking on the \"About Pathlines\" icon. ")
                      ])
                    ], -1)),
                    _createElementVNode("div", _hoisted_4, [
                      _createElementVNode("button", {
                        class: "btn btn-primary",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (onAboutClicked('https://www.pathlines.co.uk/')))
                      }, "About PathLines")
                    ])
                  ])
                ])
              ]),
              _: 1
            }, 8, ["title"]),
            _createVNode(SubSection, {
              title: "",
              showHelp: false
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _cache[12] || (_cache[12] = _createElementVNode("div", { class: "col-md-2 d-flex justify-content-center align-items-center" }, [
                      _createElementVNode("a", {
                        class: "navbar-brand skin-navbar-brand p-0",
                        style: {"background-repeat":"no-repeat"},
                        target: "_blank"
                      }, [
                        _createElementVNode("span", { class: "visually-hidden" }, "Guiide")
                      ])
                    ], -1)),
                    _cache[13] || (_cache[13] = _createElementVNode("div", { class: "col-md-8 ps-4" }, [
                      _createElementVNode("p", null, " To be clear Guiide will not be your product provider, it will only be a platform for you to model a plan, your pension will not be with Guiide it will be with Pathlines. "),
                      _createElementVNode("p", null, " We support tens of thousands of savers like you with our planning tools to build an initial sustainable plan and we will continue to support you with our tracking tools up to, at and through into retirement. ")
                    ], -1)),
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("button", {
                        class: "btn btn-primary",
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (onAboutClicked('https://www.corp.guiide.co.uk/index')))
                      }, "About us")
                    ])
                  ])
                ])
              ]),
              _: 1
            }),
            _createVNode(PensionPotsViewer, {
              ref: "pensionPotViewer",
              title: "Pensions to Transfer",
              "show-valuation": false,
              "show-est-retirement-value": false,
              showAddButton: false,
              showEditButtons: false,
              showAdvice: false,
              showHelp: false,
              selectionEnabled: true,
              selectedColumnTitle: "Transfer",
              "show-pathlines-alert": true,
              "show-present-total-value": true,
              "selected-pension-pot-ids": selectedPensionPotIds.value,
              "onUpdate:selectedPensionPotIds": _cache[2] || (_cache[2] = ($event: any) => ((selectedPensionPotIds).value = $event)),
              "selectable-pension-pot-ids": eligiblePensionPotIds.value,
              "current-value-help-text": pensionsViewerCurrentValueHelpText
            }, {
              "lower-area": _withCtx(() => [
                _createElementVNode("div", {
                  class: _normalizeClass(["alert my-2", `alert-primary`]),
                  role: "alert"
                }, _cache[14] || (_cache[14] = [
                  _createElementVNode("i", { class: "bi bi-exclamation-circle-fill me-2" }, null, -1),
                  _createTextVNode(" Please note Pathlines cannot accept any transfers where cash or income has already been accessed from them. They also cannot accept any transfers where your employer is also making contributions, or your pot has any valuable guaranteed benefits such as a fixed annuity rate as you will lose these employer contributions or guaranteed benefits. Pathlines will check this as part of their transfer process to ensure this is not the case and may inform you some pots may not be transferable to them. ")
                ]))
              ]),
              _: 1
            }, 8, ["selected-pension-pot-ids", "selectable-pension-pot-ids"]),
            _createVNode(SubSection, {
              title: "Modelling investment funds",
              showHelp: false
            }, {
              default: _withCtx(() => _cache[15] || (_cache[15] = [
                _createElementVNode("p", { class: "fw-bold" }, "There are many type of investment funds, but very broadly they can be split into three types:", -1),
                _createElementVNode("p", null, [
                  _createElementVNode("strong", null, "Money (Cash) funds"),
                  _createTextVNode(" - Expected returns come from short term interest like payments. Keeping some of your pension pot in these funds can be a good idea as it means your immediate income and cash lump needs can always be met. This can help when markets are down as it means you don't need to sell investments when prices are low. ")
                ], -1),
                _createElementVNode("p", null, [
                  _createElementVNode("strong", null, "Income funds"),
                  _createTextVNode(" - Expected returns come largely from income payments. This type of fund usually keeps paying an income even if markets drop. This can also help you avoid having to sell investments during any downturn and help to protect your pension pot value over the long term. ")
                ], -1),
                _createElementVNode("p", null, [
                  _createElementVNode("strong", null, "Growth funds"),
                  _createTextVNode(" - Expected returns come largely from market growth. For your pension pot to last a lifetime, you'll need it to grow. Keeping some of your pension pot invested in growth investments - such as funds that invest in company shares - can help you grow your pension pot. However, there is risk involved and the value of shares will go up and down. If they go down they may not recover for some time, if at all. ")
                ], -1)
              ])),
              _: 1
            }),
            _createVNode(InvestmentAllocation, {
              ref_key: "investmentAllocation",
              ref: investmentAllocation,
              retiree: retiree.value,
              "selected-pension-pot-ids": selectedPensionPotIds.value,
              onInvalid: investmentAllocationsInvalid
            }, null, 8, ["retiree", "selected-pension-pot-ids"]),
            _createVNode(SubSection, {
              title: "Important points to consider",
              showHelp: false
            }, {
              default: _withCtx(() => [
                _cache[18] || (_cache[18] = _createElementVNode("p", null, " Annually reviewing and tracking your plan is key. At any annual review (in March each year), you can change your future payments and investment fund choices if you wish to. ", -1)),
                _cache[19] || (_cache[19] = _createElementVNode("p", null, [
                  _createTextVNode(" The above allows you to better understand the investment risks involved. It is not a suggestion as to how much risk is appropriate for you, or how you should invest. "),
                  _createElementVNode("strong", null, "If you require a recommendation on these points you must seek independent financial advice.")
                ], -1)),
                _createElementVNode("p", null, [
                  _createTextVNode(" We can pass the modelling above to Pathlines to help with the " + _toDisplayString(_unref(Constants).Product.Name) + " signup process. ", 1),
                  _cache[16] || (_cache[16] = _createElementVNode("strong", null, "However any actual investment choice confirmation must be made in the Pathlines application process.", -1)),
                  _cache[17] || (_cache[17] = _createTextVNode(" More information on the three current funds available is provided within the Pathlines application process. "))
                ]),
                _createElementVNode("p", null, " There are currently only three available funds (one of each) to choose from in the " + _toDisplayString(_unref(Constants).Product.Name) + " product. In future, Pathlines may add alternative funds and investment managers to choose from, or switch to, within " + _toDisplayString(_unref(Constants).Product.Name) + ". Other providers may offer a much wider range of funds and investment choices. These may be more suitable for your circumstances. Other providers can also be used alongside " + _toDisplayString(_unref(Constants).Product.Name) + " if you choose. ", 1),
                _createElementVNode("p", null, " Regarding investment allocations, Pathlines will first invest " + _toDisplayString(_unref(FundAllocationCalculator).TrusteeAccountPercentage) + "% of the total transfer value in the " + _toDisplayString(_unref(fundInfo).trusteeBankAccount.name) + ", then the amount you confirm to Pathlines you want in the " + _toDisplayString(_unref(fundInfo).moneyFund.name) + ", followed by the " + _toDisplayString(_unref(fundInfo).incomeFund.name) + ". Finally any residual amount (if any) will be invested in the " + _toDisplayString(_unref(fundInfo).responsibleGrowthFund.name) + ", until everything transferred to Pathlines is allocated. ", 1),
                _createElementVNode("p", null, " Guiide and Pathlines have no commercial relationship with any investment manager who provides funds in the " + _toDisplayString(_unref(Constants).Product.Name) + " product. To be completely clear Guiide does have a commercial interest in the success of the " + _toDisplayString(_unref(Constants).Product.Name) + " product and will receive some ongoing payments from Pathlines should you choose it. ", 1)
              ]),
              _: 1
            }),
            _createVNode(SubSection, {
              title: "Questions you may have",
              showHelp: false
            }, {
              default: _withCtx(() => [
                _cache[20] || (_cache[20] = _createElementVNode("p", null, " We have detailed commonly asked questions below but if you would like to know anything not covered here please email us at contact@guiide.co.uk. ", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(questionAndAnswerQuestions(), (question, i) => {
                  return (_openBlock(), _createBlock(QuestionAndAnswer, {
                    key: i,
                    "show-answer": question.showAnswerByDefault,
                    "show-caret": !question.showAnswerByDefault,
                    "show-border": false
                  }, {
                    question: _withCtx(() => [
                      _createTextVNode(_toDisplayString(question.question), 1)
                    ]),
                    default: _withCtx(() => [
                      _createElementVNode("p", {
                        innerHTML: question.answer
                      }, null, 8, _hoisted_8)
                    ]),
                    _: 2
                  }, 1032, ["show-answer", "show-caret"]))
                }), 128))
              ]),
              _: 1
            }),
            (isEligibleForProduct.value && productLive.value)
              ? (_openBlock(), _createBlock(Apply, {
                  key: 0,
                  ref_key: "apply",
                  ref: apply,
                  "is-applying": isApplying.value,
                  "advice-message": applyErrorMessage.value,
                  onApply: onApply
                }, null, 8, ["is-applying", "advice-message"]))
              : _createCommentVNode("", true)
          ], 64)),
      _cache[22] || (_cache[22] = _createElementVNode("p", null, "You can seek other providers on your own or get independent financial advice.", -1)),
      _cache[23] || (_cache[23] = _createElementVNode("p", { class: "mb-5" }, [
        _createTextVNode("You can do this via our Advisers page, or go to "),
        _createElementVNode("a", {
          href: "https://www.moneyhelper.org.uk",
          target: "_blank"
        }, "moneyhelper.org.uk"),
        _createTextVNode(" if you want more help on finding an adviser.")
      ], -1)),
      _createElementVNode("button", {
        class: "btn btn-secondary",
        onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('back')))
      }, "Back")
    ])
  ], 64))
}
}

})